/**
 * Скачивание аудио-файла с embedded ID3 тэгом обложки.
 *
 * Suno отдаёт MP3 и cover-image раздельно — в скачиваемом MP3 нет embedded
 * artwork, поэтому плееры (iTunes, Apple Music, Яндекс.Музыка, MediaPlayer
 * на Android) не показывают обложку. Чиним клиентски через browser-id3-writer:
 *
 *   1. Параллельно fetch(audio) + fetch(cover)
 *   2. Пишем ID3v2 APIC frame (album art) поверх MP3 buffer'а
 *   3. Сохраняем новый blob юзеру
 *
 * Для WAV ID3v2 тоже работает — стандарт разрешает RIFF-контейнеру иметь ID3
 * chunk, большинство плееров его читают. Но если embedding не удался (CORS
 * на cover, не-MP3 формат) — fallback на plain-download без обложки.
 */
import { ID3Writer } from 'browser-id3-writer'
import { showToast } from '../../../components/Toast'

export interface DownloadOptions {
	/** Название трека — пишется в ID3 TIT2 frame + в имя файла. */
	title?: string | null
	/** Исполнитель — ID3 TPE1 frame. Для Suno-треков это сам юзер/проект,
	 *  можно проставлять «Umnik AI» или бренд платформы. */
	artist?: string | null
	/** Альбом — ID3 TALB frame. Опционально. */
	album?: string | null
	/** Теги/жанр — ID3 TCON. Пишем если есть track.tags. */
	genre?: string | null
}

/** Fetch binary и возвращает ArrayBuffer либо null если упало. */
async function fetchBinary(url: string): Promise<ArrayBuffer | null> {
	try {
		const r = await fetch(url)
		if (!r.ok) return null
		return await r.arrayBuffer()
	} catch {
		return null
	}
}

/**
 * Скачивает аудио-файл и (если cover указан) вшивает в ID3v2 тэг обложку
 * + метаданные. Если embedding провалился или cover не передан — скачивает
 * исходный файл как есть.
 *
 * Работает и с MP3 и с WAV (WAV+ID3 — de-facto стандарт у большинства плееров).
 *
 * @returns URL запаковки (тот что был отдан браузеру). Может пригодиться для
 *   отображения в UI, хоть обычно мы просто скачиваем.
 */
export async function downloadAudioWithCover(
	audioUrl: string,
	coverUrl: string | null | undefined,
	filename: string,
	options: DownloadOptions = {},
): Promise<void> {
	const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
	if (isIOS) showToast('Готовим файл с обложкой...', 'info')

	// 1. Скачиваем аудио
	const audioBuffer = await fetchBinary(audioUrl)
	if (!audioBuffer) {
		// Байты недоступны из JS. Если файл на ЧУЖОМ домене — это почти всегда
		// CORS (WAV у Mureka лежит на её CDN, а не в нашем S3): вшить обложку
		// нельзя, но отдать файл можно — прямой ссылке разрешение не нужно,
		// ровно как у пилюли «Скачать». Путь Suno (файлы у нас, fetch проходит)
		// сюда не попадает — там по-прежнему скачивание с обложкой.
		let crossOrigin = false
		try {
			crossOrigin = new URL(audioUrl, window.location.href).origin !== window.location.origin
		} catch {
			crossOrigin = false
		}
		if (!crossOrigin) {
			// Свой домен и всё равно не читается — файла действительно нет
			// (истёк, удалён). Прямая ссылка отдала бы страницу ошибки вместо
			// аудио, поэтому честно говорим правду.
			showToast('Не удалось скачать аудио', 'error')
			return
		}
		const a = document.createElement('a')
		a.href = audioUrl
		a.download = filename
		a.rel = 'noopener'
		document.body.appendChild(a)
		a.click()
		a.remove()
		showToast('Скачиваем файл (обложка не встроена — файл на стороне провайдера)', 'info')
		return
	}

	// 2. Пытаемся скачать обложку — параллельно (но уже после audio, т.к.
	//    audio основная — если обложка упадёт, всё равно отдадим юзеру файл).
	let coverBuffer: ArrayBuffer | null = null
	if (coverUrl) {
		coverBuffer = await fetchBinary(coverUrl)
	}

	// 3. Строим blob: либо с ID3-tag'ом, либо plain.
	let finalBlob: Blob
	const contentType = filename.toLowerCase().endsWith('.wav') ? 'audio/wav' : 'audio/mpeg'

	if (coverBuffer) {
		try {
			const writer = new ID3Writer(audioBuffer)
			if (options.title) writer.setFrame('TIT2', options.title)
			if (options.artist) writer.setFrame('TPE1', [options.artist])
			if (options.album) writer.setFrame('TALB', options.album)
			if (options.genre) writer.setFrame('TCON', [options.genre])
			writer.setFrame('APIC', {
				type: 3, // Front cover
				data: coverBuffer,
				description: 'Cover',
			})
			writer.addTag()
			finalBlob = writer.getBlob()
		} catch (err) {
			// Не смогли записать ID3 — отдаём оригинал чтобы юзер не остался без файла.
			console.warn('[audioWithCover] ID3 embed failed, falling back to plain:', err)
			finalBlob = new Blob([audioBuffer], { type: contentType })
		}
	} else {
		finalBlob = new Blob([audioBuffer], { type: contentType })
	}

	// 4. Отдаём юзеру
	try {
		if (isIOS) {
			const shareFile = new File([finalBlob], filename, { type: contentType })
			if (navigator.canShare?.({ files: [shareFile] })) {
				try {
					await navigator.share({ files: [shareFile] })
				} catch (e) {
					if (e instanceof Error && e.name !== 'AbortError') throw e
				}
				return
			}
			// Fallback — без share API открываем URL, но юзер теряет обложку.
			window.open(audioUrl, '_blank')
			return
		}
		const objectUrl = URL.createObjectURL(finalBlob)
		const a = document.createElement('a')
		a.href = objectUrl
		a.download = filename
		document.body.appendChild(a)
		a.click()
		a.remove()
		setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
	} catch {
		showToast('Не удалось сохранить файл', 'error')
	}
}

/** Безопасное имя файла из title — убираем запрещённые символы. */
export function safeFilename(title: string | null | undefined, ext: 'mp3' | 'wav' | 'flac'): string {
	const base = (title?.trim() || 'track')
		.replace(/[\\/:"*?<>|]/g, '_')
		.slice(0, 100)
	return `${base}.${ext}`
}
